import type { EntityStateEvents } from 'shared/channels/entity';
import type { GridFilterModel } from '@mui/x-data-grid-pro';
import { defineChannelContract } from 'shared/channels';
import type { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type {
  DocumentationCreateModel,
  DocumentationDeleteModel,
  DocumentationEditModel,
  DocumentationModel,
  DocumentationMoveModel,
  DocumentationVersionEditModel,
} from './documentationModel';
import type { GetDocumentationSignedRequestModel } from './getDocumentationSignedRequestModel';
import type {
  DocumentationOperationPreflightRequest,
  DocumentationOperationPreflightResponse,
} from './operationPreflight';
import type { SearchedDocumentationsRequestData } from './searchedDocumentationsRequest';

export interface FilteredDirectoryRequest {
  parentId: string;
  filterModel: GridFilterModel;
  /** An `EntityState` value; kept a plain string so `shared` does not depend on the UI. */
  documentState: string;
  timezone: string;
  organizationId: string | null;
}

export interface FilteredDirectoryResponse {
  directories: DirectoryModel[];
  documentations: DocumentationModel[];
}

/** `uploadedEntity`: a documentation the server stored. */
export interface UploadedDocumentationPayload {
  localId: number;
  _id: string;
  versionId?: string;
  parentId?: string;
}

/** Signed URLs of one documentation version. */
export interface DocumentationSignedUrls {
  signedRequest: string;
  imageSignedRequest?: string;
  thumbnailSignedRequest?: string;
  mergedSignedRequest?: string;
  mergedThumbnailSignedRequest?: string;
  drawingSignedRequest?: string;
  imageRotation?: number;
}

export interface DocumentationRestoreRequest {
  documentations: number[];
  directories: number[];
  signature?: string;
}

/** Documentation requests answered by the service worker and the events it broadcasts. */
export const documentationContract = defineChannelContract<{
  requests: {
    getOneById: { request: { id: string }; response: DocumentationModel };
    getChildren: { request: { parentId: string }; response: DocumentationModel[] };
    getFilteredDirectory: { request: FilteredDirectoryRequest; response: FilteredDirectoryResponse };
    search: { request: SearchedDocumentationsRequestData; response: DocumentationModel[] };
    preflight: {
      request: DocumentationOperationPreflightRequest;
      response: DocumentationOperationPreflightResponse;
    };
    /**
     * Saves the documentation locally and queues its upload; responds with the
     * local id. `uniqueId` comes back with the `createdEntity` event.
     */
    create: { request: { documentation: DocumentationCreateModel; uniqueId?: string }; response: number };
    edit: { request: DocumentationEditModel; response: void };
    editVersion: { request: DocumentationVersionEditModel; response: void };
    remove: { request: DocumentationDeleteModel; response: void };
    restore: { request: DocumentationRestoreRequest; response: void };
    setParent: { request: DocumentationMoveModel; response: void };
    sync: { request: undefined; response: void };
    /** Rejects when the server returns no signed URL. */
    getSignedUrl: { request: GetDocumentationSignedRequestModel; response: DocumentationSignedUrls };
  };
  events: EntityStateEvents & {
    [DomainMessagesTypes.createdEntity]: number[];
    [DomainMessagesTypes.updatedEntity]: unknown;
    [DomainMessagesTypes.uploadedEntity]: UploadedDocumentationPayload;
    /** The `localId` of the item whose upload failed, or none with an `error` of the queue. */
    [DomainMessagesTypes.failUploadedEntity]: number | undefined;
    // Payloads below differ between the worker's producers; typed once those are.
    [DomainMessagesTypes.documentationMoved]: unknown;
    [DomainMessagesTypes.documentationDeleted]: unknown;
    [DomainMessagesTypes.documentationRestored]: unknown;
    [DomainMessagesTypes.allDocumentations]: { hasAll: boolean };
  };
}>('documentationChannel');
